/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Clock, Play, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import { ServiceInfo } from './types.ts';
import { convertirHeureEnMinutes } from './logiquePure.ts';

interface BarreTempsProps {
  heureAffichee: string;
  heureReelle: string;
  service: ServiceInfo;
  onChangementHeure: (nouvelleHeure: string) => void;
  onRevenirMaintenant: () => void;
}

export const BarreTemps: React.FC<BarreTempsProps> = ({
  heureAffichee,
  heureReelle,
  service,
  onChangementHeure,
  onRevenirMaintenant,
}) => {
  const minDebut = convertirHeureEnMinutes(service.debut);
  const minFin = convertirHeureEnMinutes(service.fin);
  const minAffichee = convertirHeureEnMinutes(heureAffichee);

  const formaterMinutesEnHeure = (totalMinutes: number): string => {
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const gererGlissiere = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    onChangementHeure(formaterMinutesEnHeure(val));
  };

  const avancer15 = () => {
    const nouv = Math.min(minFin, minAffichee + 15);
    onChangementHeure(formaterMinutesEnHeure(nouv));
  };

  const reculer15 = () => {
    const nouv = Math.max(minDebut, minAffichee - 15);
    onChangementHeure(formaterMinutesEnHeure(nouv));
  };

  const estMaintenant = heureAffichee === heureReelle;

  return (
    <div
      className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-3 sm:px-4 py-2 sm:py-2.5 flex flex-wrap items-center justify-between gap-2 text-sm select-none"
      role="region"
      aria-label="Barre temporelle du service"
    >
      {/* Service & Heure courante */}
      <div className="flex items-center gap-2 sm:gap-3">
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 font-semibold text-xs sm:text-sm">
          <Clock className="w-4 h-4 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
          <span>{service.nom}</span>
          <span className="text-slate-400 dark:text-slate-500 font-normal">
            ({service.debut} - {service.fin})
          </span>
        </div>

        <div className="flex items-center gap-1">
          <span className="text-xs text-slate-500 dark:text-slate-400">Plan à :</span>
          <span className="font-mono font-bold text-base text-slate-900 dark:text-slate-100 px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200">
            {heureAffichee}
          </span>
        </div>
      </div>

      {/* Curseur temporel par pas de 15 minutes */}
      <div className="flex-1 min-w-[200px] max-w-md mx-2 flex items-center gap-2">
        <button
          type="button"
          onClick={reculer15}
          disabled={minAffichee <= minDebut}
          aria-label="Reculer de 15 minutes"
          className="p-1 rounded text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed min-w-[36px] min-h-[36px] flex items-center justify-center cursor-pointer transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <div className="flex-1 flex flex-col justify-center">
          <input
            type="range"
            min={minDebut}
            max={minFin}
            step={15}
            value={minAffichee}
            onChange={gererGlissiere}
            aria-label="Curseur d'heure du service (par 15 minutes)"
            className="w-full accent-slate-900 dark:accent-emerald-400 cursor-pointer h-2 bg-slate-200 dark:bg-slate-700 rounded-lg"
          />
          <div className="flex justify-between text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 px-0.5 font-mono">
            <span>{service.debut}</span>
            <span>{service.fin}</span>
          </div>
        </div>

        <button
          type="button"
          onClick={avancer15}
          disabled={minAffichee >= minFin}
          aria-label="Avancer de 15 minutes"
          className="p-1 rounded text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed min-w-[36px] min-h-[36px] flex items-center justify-center cursor-pointer transition-colors"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Bouton Maintenant */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onRevenirMaintenant}
          disabled={estMaintenant}
          aria-label="Revenir à l'heure actuelle du restaurant"
          className={`px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5 cursor-pointer min-h-[36px] transition-all ${
            estMaintenant
              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700'
              : 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 hover:opacity-90 active:scale-95 shadow-xs'
          }`}
        >
          {estMaintenant ? (
            <>
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span>En direct ({heureReelle})</span>
            </>
          ) : (
            <>
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Maintenant ({heureReelle})</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};
